// src/app/(dashboard)/meter/[peaNo]/utils/peaTariff.ts
//
// PEA residential tariff (ประเภท 1 บ้านอยู่อาศัย), effective for bills from
// September 2569 onward. Verified 2026-09 against PEA's own announcement
// (https://www.pea.co.th/news/corporate-news/2412) for the tier table, plus
// a secondary explainer for the service-charge figures (not stated on the
// primary PEA page). Ft is set by the ERC roughly every 4 months, so
// FT_BAHT_PER_KWH will drift out of date — it is not fetched live.

export const FT_BAHT_PER_KWH = 0.1623; // Sep–Dec 2569
export const VAT_RATE = 0.07;

interface TariffTier {
  upTo: number | null; // cumulative unit count this band ends at; null = unbounded
  rate: number; // บาท/หน่วย
}

// 1.1.1: total monthly usage <=150 units — includes a subsidized first band.
const TIER_1_1_1: TariffTier[] = [
  { upTo: 15, rate: 2.3488 },
  { upTo: 25, rate: 2.9882 },
  { upTo: 200, rate: 3.0 },
  { upTo: 400, rate: 4.1584 },
  { upTo: null, rate: 4.3583 },
];
const SERVICE_CHARGE_1_1_1 = 8.19;

// 1.1.2: total monthly usage >150 units — no subsidized first band.
const TIER_1_1_2: TariffTier[] = [
  { upTo: 200, rate: 3.0 },
  { upTo: 400, rate: 4.1584 },
  { upTo: null, rate: 4.3583 },
];
const SERVICE_CHARGE_1_1_2 = 24.62;

export interface TariffBand {
  from: number; // 1-based start of this band
  to: number | null; // end of band (inclusive); null = unbounded
  rate: number; // บาท/หน่วย
}

// The full tier schedule for a category, regardless of how much has
// actually been used — for UI that shows "where does my usage sit within
// the tiers" (e.g. a tier gauge), as opposed to tierBreakdown's "what was
// actually billed so far".
export function getTariffBands(category: "1.1.1" | "1.1.2"): TariffBand[] {
  const tiers = category === "1.1.1" ? TIER_1_1_1 : TIER_1_1_2;
  let lastBound = 0;
  return tiers.map((t) => {
    const band: TariffBand = { from: lastBound + 1, to: t.upTo, rate: t.rate };
    lastBound = t.upTo ?? lastBound;
    return band;
  });
}

export interface TariffTierBreakdown {
  from: number; // 1-based start of this band, for display
  to: number | null; // end of band (inclusive); null = unbounded
  rate: number; // บาท/หน่วย
  units: number; // units actually billed in this band
  charge: number; // units * rate
}

function computeTierBreakdown(
  units: number,
  tiers: TariffTier[],
): TariffTierBreakdown[] {
  let remaining = units;
  let lastBound = 0;
  const breakdown: TariffTierBreakdown[] = [];

  for (const tier of tiers) {
    if (remaining <= 0) break;
    const bandSize = tier.upTo === null ? remaining : tier.upTo - lastBound;
    const used = Math.min(remaining, bandSize);
    if (used > 0) {
      breakdown.push({
        from: lastBound + 1,
        to: tier.upTo,
        rate: tier.rate,
        units: used,
        charge: used * tier.rate,
      });
    }
    remaining -= used;
    lastBound = tier.upTo ?? lastBound;
  }

  return breakdown;
}

export interface EstimatedBill {
  category: "1.1.1" | "1.1.2";
  units: number;
  tierBreakdown: TariffTierBreakdown[];
  energyCharge: number;
  ftCharge: number;
  serviceCharge: number;
  subtotal: number;
  vat: number;
  total: number;
}

// Category is picked from the usage figure itself (there is no separate
// "declared" monthly total), which is exact for a real closed month and an
// approximation for a still-open billing cycle — the caller's UI already
// discloses that this whole figure is an estimate.
export function estimatePeaResidentialBill(units: number): EstimatedBill | null {
  if (!Number.isFinite(units) || units < 0) return null;

  const category = units <= 150 ? "1.1.1" : "1.1.2";
  const tiers = category === "1.1.1" ? TIER_1_1_1 : TIER_1_1_2;
  const serviceCharge =
    category === "1.1.1" ? SERVICE_CHARGE_1_1_1 : SERVICE_CHARGE_1_1_2;

  const tierBreakdown = computeTierBreakdown(units, tiers);
  const energyCharge = tierBreakdown.reduce((sum, t) => sum + t.charge, 0);
  const ftCharge = units * FT_BAHT_PER_KWH;
  const subtotal = energyCharge + ftCharge + serviceCharge;
  const vat = subtotal * VAT_RATE;
  const total = subtotal + vat;

  return {
    category,
    units,
    tierBreakdown,
    energyCharge,
    ftCharge,
    serviceCharge,
    subtotal,
    vat,
    total,
  };
}
